/**
 * Wildebeast wishlist
 * ---------------------------------------------------------------------------
 * A dependency-free, app-free wishlist built on localStorage.
 *
 *   <wb-wishlist-button product-handle="..." product-id="...">
 *   <wb-wishlist-grid>    renders saved items on the wishlist page
 *   [data-wishlist-count] any element; kept in sync automatically
 *
 * Storage holds handles only. Product data (price, image, availability) is
 * fetched fresh from the storefront at render time, so a saved item can never
 * show a stale price — the single most common failure of localStorage
 * wishlists.
 */

const STORAGE_KEY = 'wb:wishlist:v1';
const CHANGE_EVENT = 'wb:wishlist:change';

const Wishlist = {
  /** @returns {string[]} saved product handles, newest first */
  all() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch {
      // Private-mode Safari and blocked storage both land here.
      return [];
    }
  },

  has(handle) {
    return this.all().includes(handle);
  },

  toggle(handle) {
    const items = this.all();
    const index = items.indexOf(handle);

    if (index === -1) items.unshift(handle);
    else items.splice(index, 1);

    this.save(items);
    return index === -1; // true when it was just added
  },

  remove(handle) {
    this.save(this.all().filter((h) => h !== handle));
  },

  clear() {
    this.save([]);
  },

  save(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* Storage full or blocked — the UI still updates for this session. */
    }
    document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
  },
};

/** Keeps every [data-wishlist-count] badge in sync. */
function syncCounts() {
  const count = Wishlist.all().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
    node.textContent = String(count);
    node.hidden = count === 0;
  });
}

document.addEventListener(CHANGE_EVENT, syncCounts);
document.addEventListener('DOMContentLoaded', syncCounts);
// Reflect changes made in another tab.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) {
    syncCounts();
    document.querySelectorAll('wb-wishlist-button').forEach((el) => el.render());
    document.querySelectorAll('wb-wishlist-grid').forEach((el) => el.load());
  }
});

/* ===========================================================================
   Save button
   =========================================================================== */

class WbWishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('product-handle');
    if (!this.handle) return;

    this.button = this.querySelector('button');
    if (!this.button) return;

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation(); // don't trigger the surrounding product-card link

      const added = Wishlist.toggle(this.handle);
      this.render();

      if (added) this.burst();
      this.announce(added);
    });

    this.render();
    document.addEventListener(CHANGE_EVENT, () => this.render());
  }

  render() {
    const saved = Wishlist.has(this.handle);
    this.dataset.saved = String(saved);
    this.button.setAttribute('aria-pressed', String(saved));

    const label = saved
      ? this.getAttribute('label-saved') || 'Saved'
      : this.getAttribute('label-default') || 'Save';

    this.button.setAttribute('aria-label', `${label}: ${this.getAttribute('product-title') || ''}`.trim());
    const text = this.querySelector('[data-wishlist-label]');
    if (text) text.textContent = label;
  }

  /** Small particle burst on save — the moment worth celebrating. */
  burst() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.classList.remove('wb-wish--burst');
    void this.offsetWidth;
    this.classList.add('wb-wish--burst');
  }

  /** Screen-reader feedback, since the only visual change is an icon fill. */
  announce(added) {
    let live = document.getElementById('wb-wishlist-live');
    if (!live) {
      live = document.createElement('div');
      live.id = 'wb-wishlist-live';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      live.className = 'wb-visually-hidden';
      document.body.appendChild(live);
    }
    const title = this.getAttribute('product-title') || 'Item';
    live.textContent = added ? `${title} added to wishlist` : `${title} removed from wishlist`;
  }
}

/* ===========================================================================
   Wishlist grid
   =========================================================================== */

class WbWishlistGrid extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-items]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.loading = this.querySelector('[data-wishlist-loading]');
    this.actions = this.querySelector('[data-wishlist-actions]');
    this.root = window.Shopify?.routes?.root || '/';

    this.querySelector('[data-wishlist-clear]')?.addEventListener('click', () => {
      if (!confirm(this.dataset.clearConfirm || 'Remove everything from your wishlist?')) return;
      Wishlist.clear();
      this.load();
    });

    this.load();
    document.addEventListener(CHANGE_EVENT, () => this.load());
  }

  async load() {
    const handles = Wishlist.all();

    this.toggleState({ loading: handles.length > 0, empty: handles.length === 0 });
    if (!handles.length) {
      if (this.grid) this.grid.innerHTML = '';
      return;
    }

    // Fetch each product's live JSON in parallel. Handles that 404 (product
    // deleted or unpublished) are dropped from storage rather than rendered
    // as a broken card.
    const results = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${this.root}products/${handle}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) return null;
          return await response.json();
        } catch {
          return null;
        }
      })
    );

    const found = [];
    results.forEach((product, index) => {
      if (product) found.push(product);
      else Wishlist.remove(handles[index]);
    });

    this.toggleState({ loading: false, empty: found.length === 0 });
    if (this.grid) this.grid.innerHTML = found.map((p) => this.card(p)).join('');
    this.wireCards();
  }

  toggleState({ loading, empty }) {
    if (this.loading) this.loading.hidden = !loading;
    if (this.empty) this.empty.hidden = !empty;
    if (this.actions) this.actions.hidden = empty;
  }

  money(cents) {
    const amount = cents / 100;
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch {
      return `${currency} ${amount}`;
    }
  }

  card(product) {
    const image = product.featured_image
      ? `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=600`
      : '';
    const available = product.available;
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    const onSale = product.compare_at_price > product.price;

    return `
      <li class="wb-wish__card">
        <a class="wb-wish__media" href="${this.root}products/${product.handle}">
          ${
            image
              ? `<img src="${image}" alt="${(product.featured_image_alt || product.title).replace(/"/g, '&quot;')}"
                   width="600" height="750" loading="lazy" decoding="async">`
              : ''
          }
          ${!available ? '<span class="wb-wish__badge">Sold out</span>' : ''}
        </a>

        <div class="wb-wish__info">
          <a class="wb-wish__title" href="${this.root}products/${product.handle}">${product.title}</a>
          <p class="wb-wish__price">
            ${this.money(product.price)}
            ${onSale ? `<s>${this.money(product.compare_at_price)}</s>` : ''}
          </p>

          <div class="wb-wish__buttons">
            <button
              type="button"
              class="wb-wish__add"
              data-add-to-cart="${variant?.id || ''}"
              ${!available || !variant ? 'disabled' : ''}
            >
              ${available ? 'Add to cart' : 'Sold out'}
            </button>
            <button type="button" class="wb-wish__remove" data-remove="${product.handle}" aria-label="Remove ${product.title} from wishlist">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>
            </button>
          </div>
        </div>
      </li>`;
  }

  wireCards() {
    this.querySelectorAll('[data-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        Wishlist.remove(button.dataset.remove);
        // load() re-runs via the change event.
      });
    });

    this.querySelectorAll('[data-add-to-cart]').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = button.dataset.addToCart;
        if (!id) return;

        const original = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding…';

        try {
          const response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
          });
          if (!response.ok) throw new Error('Add to cart failed');

          button.textContent = 'Added ✓';
          // Let the theme's cart drawer and the header badge update.
          document.dispatchEvent(new CustomEvent('cart:updated'));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch {
          button.textContent = 'Try again';
        } finally {
          setTimeout(() => {
            button.textContent = original;
            button.disabled = false;
          }, 1800);
        }
      });
    });
  }
}

if (!customElements.get('wb-wishlist-button')) customElements.define('wb-wishlist-button', WbWishlistButton);
if (!customElements.get('wb-wishlist-grid')) customElements.define('wb-wishlist-grid', WbWishlistGrid);

window.WbWishlist = Wishlist;
